class StickyAtc {
  constructor(el) {
    this.el = el;
    this.sectionId = el.dataset.sectionId;
    this.position = el.dataset.position || 'top';

    this.mainForm = document.getElementById(`product-form-${this.sectionId}`);
    this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    // Dawn's quantity input is outside the <form> element (linked via form=""), so querySelector on the form returns null.
    // Look it up by ID first, fall back to inside-form search for custom themes.
    this.mainQtyInput =
      document.getElementById(`Quantity-${this.sectionId}`) ||
      this.mainForm?.querySelector('.quantity__input');

    if (!this.mainButton || !this.mainForm) return;

    this.variantEl = el.querySelector('[data-sticky-atc-variant]');
    this.priceEl = el.querySelector('[data-sticky-atc-price]');
    this.buttonEl = el.querySelector('[data-sticky-atc-button]');
    this.buttonTextEl = el.querySelector('[data-sticky-atc-button-text]');
    this.spinnerEl = el.querySelector('.loading__spinner');
    this.stickyQtyWrapper = el.querySelector('[data-sticky-atc-qty-input]');
    this.stickyQtyInput = this.stickyQtyWrapper?.querySelector('.quantity__input');
    this.purchaseOptionEl = el.querySelector('[data-sticky-atc-purchase-option]');

    this.setupObserver();
    this.setupQuantitySync();
    this.setupVariantListener();
    this.setupLoadingState();
    this.setupPurchaseOptionSync();
  }

  setupObserver() {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const buttonVisible = entry.isIntersecting;
        this.el.classList.toggle('is-visible', !buttonVisible);
        this.el.setAttribute('aria-hidden', buttonVisible ? 'true' : 'false');
        this.updateBackToTopOffset(!buttonVisible);
      },
      { threshold: 0 }
    );
    observer.observe(this.mainButton);
  }

  updateBackToTopOffset(stickyVisible) {
    if (this.position !== 'bottom') return;
    const offset = stickyVisible ? `${this.el.offsetHeight}px` : '0px';
    document.documentElement.style.setProperty('--sticky-atc-offset', offset);
  }

  setupQuantitySync() {
    if (!this.stickyQtyInput || !this.mainQtyInput) return;

    // sticky → main
    this.stickyQtyInput.addEventListener('change', () => {
      this.mainQtyInput.value = this.stickyQtyInput.value;
      this.mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // main → sticky
    this.mainQtyInput.addEventListener('change', () => {
      this.stickyQtyInput.value = this.mainQtyInput.value;
    });
  }

  setupVariantListener() {
    if (typeof subscribe === 'undefined') return;

    subscribe('variant-change', ({ data }) => {
      if (data.sectionId !== this.sectionId) return;

      const { variant } = data;

      if (!variant) {
        this.setUnavailable();
        return;
      }

      if (this.variantEl) {
        this.variantEl.textContent = variant.title === 'Default Title' ? '' : variant.title;
      }

      if (this.priceEl) {
        this.priceEl.textContent = this.formatMoney(variant.price);
      }

      // Sync quantity rules
      if (this.stickyQtyInput) {
        this.stickyQtyInput.min = variant.quantity_rule?.min ?? 1;
        this.stickyQtyInput.step = variant.quantity_rule?.increment ?? 1;
        if (variant.quantity_rule?.max) {
          this.stickyQtyInput.max = variant.quantity_rule.max;
        } else {
          this.stickyQtyInput.removeAttribute('max');
        }
        this.stickyQtyInput.value = this.mainQtyInput?.value ?? variant.quantity_rule?.min ?? 1;
      }

      if (variant.available) {
        this.buttonEl?.removeAttribute('disabled');
        if (this.buttonTextEl) {
          this.buttonTextEl.textContent = this.buttonEl?.dataset.addText || '';
        }
      } else {
        this.setUnavailable();
      }
    });
  }

  setupLoadingState() {
    this.mainForm.addEventListener('submit', () => {
      if (!this.buttonEl || !this.spinnerEl) return;
      this.buttonEl.setAttribute('aria-disabled', 'true');
      this.buttonEl.classList.add('loading');
      this.spinnerEl.classList.remove('hidden');
    });

    const reset = () => {
      if (!this.buttonEl || !this.spinnerEl) return;
      this.buttonEl.classList.remove('loading');
      this.spinnerEl.classList.add('hidden');
      if (this.buttonEl.getAttribute('aria-disabled') === 'true') {
        this.buttonEl.removeAttribute('aria-disabled');
      }
    };

    if (typeof subscribe !== 'undefined') {
      subscribe('cart-update', reset);
      subscribe('cart-error', reset);
    }
  }

  setupPurchaseOptionSync() {
    if (!this.purchaseOptionEl) return;

    this.subscriptionContainer = document.querySelector('.selling_plan_app_container');

    this.boundHandleSellingPlanChanged = (event) => {
      const variantIdInput = this.mainForm.querySelector('input[name="id"]');
      if (
        variantIdInput &&
        event.detail?.variantId &&
        String(variantIdInput.value) !== String(event.detail.variantId)
      ) {
        return;
      }
      // Firmhouse's own price render (`.total-price`) can still be mid-update when this
      // event fires, so treat it as a trigger and re-read the DOM rather than trusting
      // event.detail.price directly.
      this.syncPurchaseOptionFromDom();
    };
    document.addEventListener('firmhouse.sellingPlanChanged', this.boundHandleSellingPlanChanged);

    if (!this.subscriptionContainer) return;

    this.syncPurchaseOptionFromDom();

    // The default selected purchase option (and its price) is rendered asynchronously by
    // Firmhouse straight into the DOM without dispatching an event, so observe for that too.
    this.purchaseOptionObserver = new MutationObserver(() => {
      clearTimeout(this.purchaseOptionSyncTimeout);
      this.purchaseOptionSyncTimeout = setTimeout(() => this.syncPurchaseOptionFromDom(), 150);
    });
    this.purchaseOptionObserver.observe(this.subscriptionContainer, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  syncPurchaseOptionFromDom() {
    const container = this.subscriptionContainer;
    if (!container) return;

    const checkedRadio = container.querySelector('input[type="radio"][data-radio-type]:checked');

    if (!checkedRadio || checkedRadio.dataset.radioType !== 'selling_plan') {
      this.setPurchaseOption(false);
      if (checkedRadio?.dataset.price) {
        this.setPrice(checkedRadio.dataset.price, checkedRadio.dataset.priceHtml);
      }
      return;
    }

    this.setPurchaseOption(true, checkedRadio);

    const totalPriceEl = checkedRadio.closest('.subscription-card')?.querySelector('.price-info .total-price');
    const totalPriceText = totalPriceEl?.textContent?.trim();
    if (totalPriceText) {
      this.setPrice(totalPriceText);
    }
  }

  setPurchaseOption(isSubscription, contextEl) {
    if (!this.purchaseOptionEl) return;

    if (!isSubscription) {
      this.purchaseOptionEl.hidden = true;
      this.purchaseOptionEl.textContent = '';
      return;
    }

    const container = contextEl?.closest('.selling_plan_app_container') || document;
    const frequencySelect = container.querySelector('.frequency-select');
    const frequencyText = frequencySelect?.options[frequencySelect.selectedIndex]?.text?.trim();
    const subscriptionLabel = this.purchaseOptionEl.dataset.subscriptionLabel || '';

    this.purchaseOptionEl.textContent = frequencyText
      ? `${subscriptionLabel} · ${frequencyText}`
      : subscriptionLabel;
    this.purchaseOptionEl.hidden = false;
  }

  setPrice(price, priceHtml) {
    if (!this.priceEl) return;
    if (price) {
      this.priceEl.textContent = price;
    } else if (priceHtml) {
      this.priceEl.textContent = priceHtml.replace(/<[^>]*>/g, '').trim();
    }
  }

  setUnavailable() {
    this.buttonEl?.setAttribute('disabled', '');
    if (this.buttonTextEl) {
      this.buttonTextEl.textContent = window.variantStrings?.soldOut || '';
    }
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.moneyFormat || '{{amount_with_comma_separator}}');
    }
    return (cents / 100).toFixed(2);
  }
}

document.querySelectorAll('.sticky-atc').forEach((el) => new StickyAtc(el));
